<script setup lang="ts">
import ContractsTable from "@/components/contracts-table.vue";
import UsersTable from "@/components/users-table.vue";
import { useLoggedInUserFlags } from "@/composables/use-logged-in-user-flags";
import type { ContractDTO } from "@/models/dtos/contract-dto";
import type { CustomerDTO } from "@/models/dtos/customer-dto";
import type { UserDTO } from "@/models/dtos/user-dto";
import { Role } from "@/models/enums/role";
import { RouteName } from "@/router";
import { customersService } from "@/services/customers-service";
import { notificationsService } from "@/services/notifications-service";
import { onBeforeMount, ref, toRefs } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();

const props = defineProps<{ customerNumber: string }>();
const { customerNumber } = toRefs(props);

const { userIsOneAdmin, userIsCustomerAdmin } = useLoggedInUserFlags(
  customerNumber.value,
);

const pageLoading = ref(false);

const customer = ref<CustomerDTO | undefined>();

onBeforeMount(async () => {
  try {
    pageLoading.value = true;
    customer.value = await customersService.getCustomerByCustomerNumber(
      customerNumber.value!,
    );
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid hämtning av kunden.",
      type: "error",
      error,
    });
  } finally {
    pageLoading.value = false;
  }
});

function onClickUser(user: UserDTO) {
  router.push({
    name: RouteName.User,
    params: {
      userId: user.id,
      customerNumber: customerNumber.value,
    },
  });
}

function onClickContract(contract: ContractDTO) {
  router.push({
    name: RouteName.Contract,
    params: {
      contractId: contract.id,
    },
  });
}
</script>

<template>
  <section class="section px-0">
    <p class="title is-4">{{ customer?.name }}</p>

    <app-loader v-if="pageLoading"></app-loader>
  </section>
  <template v-if="!pageLoading">
    <section
      v-if="customer"
      class="section notification has-background-violet-light has-text-violet-dark"
    >
      <div class="columns is-justify-content-space-between">
        <div class="column">
          <p class="title is-5">Affärer</p>
          <p class="subtitle is-6 mb-2">
            (Notera att detta Inte behöver vara alla affärer/avtal mellan Er och
            ONE Nordic)
          </p>
        </div>

        <div class="column is-narrow is-flex is-align-items-flex-end">
          <router-link
            v-if="userIsOneAdmin"
            :to="{ name: RouteName.CustomerCreateContract }"
            class="button"
            >Skapa affär</router-link
          >
        </div>
      </div>

      <contracts-table
        :customer-number="customer.customerNumber"
        :disable-click="!userIsOneAdmin"
        @click:row="onClickContract"
      ></contracts-table>
    </section>

    <section
      v-if="customer"
      class="section notification has-background-folly-light has-text-folly-dark"
    >
      <div class="columns is-justify-content-space-between">
        <div class="column">
          <p class="title is-5">Kundadministratörer</p>
          <p class="subtitle is-6 mb-2">
            (Behöver ni fler kundadministratörer eller ändra uppgifter kontakta
            ONE Nordic)
          </p>
        </div>

        <div class="column is-narrow is-flex is-align-items-flex-end">
          <router-link
            v-if="userIsOneAdmin"
            :to="{ name: RouteName.CustomerCreateUserAdmin }"
            class="button"
            >Skapa kundadministratör</router-link
          >
        </div>
      </div>

      <users-table
        :customer-number="customer.customerNumber"
        :role="Role.CUSTOMER_ADMIN"
        :deleteable="userIsOneAdmin"
        status-info-column
        status-action-column
        show-all-customer-users
        @click:row="onClickUser"
      ></users-table>
    </section>

    <section
      v-if="customer"
      class="section notification has-background-emerald-light has-text-emerald-dark"
    >
      <div class="columns is-justify-content-space-between">
        <div class="column"><p class="title is-5 mb-2">Användare</p></div>

        <div class="column is-narrow is-flex is-align-items-flex-end">
          <router-link
            v-if="userIsOneAdmin || userIsCustomerAdmin"
            :to="{ name: RouteName.CustomerCreateUser }"
            class="button"
            >Skapa användare</router-link
          >
        </div>
      </div>

      <users-table
        :customer-number="customer.customerNumber"
        :role="Role.USER"
        :deleteable="userIsOneAdmin || userIsCustomerAdmin"
        status-info-column
        status-action-column
        show-all-customer-users
        @click:row="onClickUser"
      ></users-table>
    </section>
  </template>
</template>
